import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../../../store";
import { useNavigate } from "react-router-dom";
import nFormatter from "../../../core/utils/n-formatter";
import { getPostStatsThunk } from "../../../store/actions/social.action";
import { useSocialContext } from "../../../context/socialContext";
import { listCommunities } from "../../../constants/communities";
import chatbotImage from "../../../assets/chat-bot-no-bg.png";

interface StatsType {
  stats: {
    post: {
      [countryCode: string]: number;
    };
  };
  posts: number;
  reactions: number;
}

export const LeftSidebar = () => {
  const me = useAppSelector((state) => state.authReducer.me);
  const navigate = useNavigate();
  const { community: selectedCommunity, setSelectedCommunity } =
    useSocialContext();
  const [stats, setStats] = useState<StatsType | null>(null);
  const dispatch = useAppDispatch();

  // Create communities variable using listCommunities and stats
  const communities = listCommunities.map((community) => ({
    ...community,
    members: nFormatter(stats?.stats?.post?.[community.countryCode] || 0, 1),
  }));

  const fetchPostStats = async () => {
    try {
      const result = await dispatch(getPostStatsThunk()).unwrap();

      if (result) {
        const stats = result as StatsType;
        setStats(stats);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleCommunityClick = (countryCode: string) => {
    setSelectedCommunity(countryCode);
    navigate("/");
  };

  useEffect(() => {
    fetchPostStats();
  }, []);

  return (
    <LeftSidebarWrapper>
      {/* Chatbot Ads Section */}
      <ChatbotAdsSection>
        <ChatbotImage src={chatbotImage} alt="AI Assistant" />
        <AdsTitle>AI Assistant</AdsTitle>
        <AdsDescription>
          Get instant help and answers to your questions
        </AdsDescription>
        <TryButton onClick={() => navigate("/chat")}>
          Start Chat
        </TryButton>
      </ChatbotAdsSection>

      {/* Communities Section */}
      <CommunitiesSectionWrapper>
        <SectionTitle>Communities</SectionTitle>
        <CommunitiesList>
          {communities.map((community, index) => (
            <CommunityItem
              key={index}
              isSelected={selectedCommunity === community.countryCode}
              onClick={() => handleCommunityClick(community.countryCode)}
            >
              <CommunityFlag>
                {community.countryCode === "KARDO" ? (
                  <img
                    src={community.flag}
                    alt={community.name}
                    style={{
                      width: "100%",
                      objectFit: "cover",
                      padding: "4px",
                      boxSizing: "border-box",
                    }}
                  />
                ) : (
                  community.flag
                )}
              </CommunityFlag>
              <CommunityInfo>
                <CommunityName>{community.name}</CommunityName>
                <CommunityMembers>{community.members} posts</CommunityMembers>
              </CommunityInfo>
            </CommunityItem>
          ))}
        </CommunitiesList>
      </CommunitiesSectionWrapper>
    </LeftSidebarWrapper>
  );
};

const LeftSidebarWrapper = styled.div`
  width: 25%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media (max-width: 1200px) {
    display: none;
  }
`;

// Chatbot Ads Section Styles
const ChatbotAdsSection = styled.div`
  background: #282828;
  border-radius: 28px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
`;

const ChatbotImage = styled.img`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  animation: float 2s ease-in-out infinite;
  width: 120px;
  height: 120px;
  object-fit: contain;

  @keyframes float {
    0% { transform: translateY(0px); }
    50% { transform: translateY(-8px); }
    100% { transform: translateY(0px); }
  }
`;

const AdsTitle = styled.h2`
  color: #fff;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
`;

const AdsDescription = styled.p`
  color: #bdbdbd;
  font-size: 1rem;
  line-height: 1.5;
  margin: 0.8rem 0 1.5rem 0;
`;

const TryButton = styled.button`
  background: linear-gradient(45deg, #ff6b6b, #ff8e53);
  color: #fff;
  border: none;
  padding: 0.8rem;
  border-radius: 10px;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  position: relative;
  overflow: hidden;
  width: 100%;
  justify-content: center;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      120deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    transition: 0.5s;
  }

  &:hover {
    box-shadow: 0 6px 20px rgba(255, 107, 107, 0.4),
      0 0 20px rgba(255, 142, 83, 0.3);
    background: linear-gradient(45deg, #ff8e53, #ff6b6b);

    &:before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px);
    box-shadow: 0 2px 10px rgba(255, 107, 107, 0.3);
  }
`;

// Communities Section Styles
const CommunitiesSectionWrapper = styled.div`
  border-radius: 24px;
`;

const SectionTitle = styled.h3`
  color: #fff;
  font-size: 1.2rem;
  font-weight: 500;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;

  .anticon {
    color: #60a5fa;
  }
`;

const CommunitiesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`;

const CommunityItem = styled.div<{ isSelected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  border-radius: 12px;
  transition: background 0.2s;
  cursor: pointer;
  padding: 0.5rem;
  background: ${(props) => (props.isSelected ? "#282828" : "transparent")};

  &:hover {
    background: #282828;
  }
`;

const CommunityFlag = styled.span`
  background: #222222;
  font-size: 1.5rem;
  width: 54px;
  height: 54px;
  border-radius: 30%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 4px solid #2b2b2b;
`;

const CommunityInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CommunityName = styled.span`
  color: #fff;
  font-weight: 500;
  font-size: 1rem;
`;

const CommunityMembers = styled.span`
  color: #bdbdbd;
  font-size: 0.9rem;
`;
